import React from "react";
import { Select, MenuItem, Box, InputLabel, FormControl, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { METRIC_UNITS, Question, Questionnaire, US_CUSTOMARY_UNITS } from "./models";
import RequiredAsterisk from "./RequiredAsterisk";

const InputHeightView = ({ question, questionnaire, onChange }: { question: Question, questionnaire: Questionnaire, onChange: (Question: Question) => void }) => {
    const { t } = useTranslation(questionnaire.cohort);
    const labelText = t(question.question);
    let cm = 0;
    let feet = 0;
    let inches = 0;
    const [cmError, setCmError] = React.useState<string>("");
    // Conversion constants
    const INCH_TO_CM = 2.54;
    const FOOT = 12;
    const cmToFeetInches = (cm: number) => {
        const totalInches = cm / INCH_TO_CM;
        const feet = Math.floor(totalInches / FOOT);
        const inches = Math.round(totalInches - feet * FOOT);
        return { feet, inches };
    };

    if (question.value && Number(question.value) > 0) {
        // question.value is in inches
        const currentValue = Number(question.value);
        if (question.unit === US_CUSTOMARY_UNITS) {
            cm = Math.round(currentValue * INCH_TO_CM);
            feet = Math.floor(currentValue / FOOT);
            inches = Math.round(currentValue % FOOT);
        } else if(question.unit === METRIC_UNITS){
            cm = Math.round(currentValue * INCH_TO_CM);
            const { feet: newFeet, inches: newInches } = cmToFeetInches(cm);
            feet = newFeet;
            inches = newInches;
        }
    } else {
        cm = 0;
        feet = 0;
        inches = 0;
    }

    // Derive unit from question or questionnaire, fallback to US
    const unit = question.unit || US_CUSTOMARY_UNITS;

    const feetOptions = Array.from({ length: 10 }, (_, i) => i.toString()); // 0-9 feet
    const inchOptions = Array.from({ length: 12 }, (_, i) => i.toString()); // 0-11 inches

    const handleMetricChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        const updatedCm = Number(input);
        if (!isNaN(updatedCm) && updatedCm >= 0 && updatedCm <= 302) {
            setCmError("");
            // convert to inches
            const updatedValue = Math.round(updatedCm / INCH_TO_CM);
            onChange({ ...question, value: `${updatedValue}` });
        } else {
            setCmError("Please enter a value between 0 and 302 centimeters.");
        }
    };

    const handleFeetChange = (event: SelectChangeEvent<string>) => {
        const updatedFeet = Number(event.target.value);
        // convert to inches
        const updatedValue = updatedFeet * FOOT + inches;
        onChange({ ...question, value: `${updatedValue}` });
    };

    const handleInchesChange = (event: SelectChangeEvent<string>) => {
        const updatedInches = Number(event.target.value);
        // convert to inches
        const updatedValue = feet * FOOT + updatedInches;
        onChange({ ...question, value: `${updatedValue}` });
    };

    return (
      <Box>
        <Box mb={2}>
          <Typography variant="h6" component='div'>
            {labelText}
            {question.required && <RequiredAsterisk />}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {unit === "1" ? (
          <TextField
              label={t("Centimeters (cm)")}
              type="text"
              value={cm.toString()}
              onChange={handleMetricChange}
              error={!!cmError}
              helperText={cmError}
              fullWidth
          />
          ) : (
          <>
            <FormControl style={{ width: 100 }}>
              <InputLabel id="feet-label" htmlFor="feet">{t("Feet")}</InputLabel>
              <Select
                  labelId="feet-label"
                  value={feet.toString()}
                  label={t("Feet")}
                  onChange={handleFeetChange}
                  inputProps={{ id: 'feet', style: { display: 'none' } }}
              >
                  {feetOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl style={{ width: 100 }}>
              <InputLabel id="inches-label" htmlFor="inches">{t("Inches")}</InputLabel>
              <Select
                  labelId="inches-label"
                  value={inches.toString()}
                  label={t("Inches")}
                  onChange={handleInchesChange}
                  inputProps={{ id: 'inches', style: { display: 'none' } }}
              >
                  {inchOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </>
          )}
        </Box>
      </Box>
    );
};

export default InputHeightView;
